import { useCallback, useRef, useState } from "react";
import { getFileBlob } from "../services/fileApi";
import { downloadBlob, parseFileNameFromContentDisposition } from "../utils/download";

/**
 * Uniwersalny hook do eksportu plików (CSV, XLSX, PDF itd.).
 * @param {string} path - np. "/Address/Csv"
 * @param {string} defaultFileName - np. "addresses.csv"
 */
export function useFileExport(path, defaultFileName) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const abortRef = useRef(null);

  const exportFile = useCallback(async (params) => {
    if (isExporting) return; // anty-spam
    setIsExporting(true);
    setExportProgress(0);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await getFileBlob(
        path,
        params,
        controller.signal,
        (progressEvent) => {
          const total = progressEvent?.total;
          const loaded = progressEvent?.loaded ?? 0;

          if (typeof total === "number" && total > 0) {
            const pct = Math.min(100, Math.round((loaded / total) * 100));
            setExportProgress(pct);
          } else if (loaded > 0) {
            setExportProgress((prev) => (prev < 90 ? prev + 5 : prev));
          }
        }
      );
      const blob = resp.data;
      
      const contentDisposition = resp.headers?.["content-disposition"];
      let filename = parseFileNameFromContentDisposition(contentDisposition);
    
      if (!filename) {
        const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const extension = defaultFileName.split('.').pop();
        const baseName = defaultFileName.replace(/\.[^/.]+$/, '');
        filename = `${baseName}_${timestamp}.${extension}`;
      }
      
      console.log("Using filename:", filename);

      downloadBlob(blob, filename);
      setExportProgress(100);
      return true;
    } catch (error) {
      console.error("Export error:", error);
      throw error;
    } finally {
      abortRef.current = null;
      setIsExporting(false);
      // Let 100% be visible for a moment, then reset.
      setTimeout(() => setExportProgress(0), 250);
    }
  }, [path, defaultFileName, isExporting]);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { exportFile, isExporting, exportProgress, cancel };
}
